import React, { useState, useEffect } from "react";
import { Link, useLocation } from 'react-router-dom';
import PrivacyPolicy from "./PrivacyPolicy";
import { appConfig } from '../../config/appConfig';

export default function Footer() {
    const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
    const location = useLocation();
    const termsLink = appConfig.documents.termsUrl;
    const calendlyUrl = appConfig.links?.calendly;
    const pathname = location.pathname;
    const industryPaths = Object.values(appConfig.routes.industryPages || {});
    const isProcessRouteActive = pathname.includes('/process');
    const isSuccessStoriesRouteActive = pathname.includes('/success-stories');
    const isIndustriesRouteActive =
        pathname === appConfig.routes.industries ||
        industryPaths.some((path) => pathname.startsWith(path));
    const isAboutRouteActive =
        pathname.includes('/about') ||
        pathname.includes('/team') ||
        pathname.includes('/faq');

    const navLinks = [
        { to: appConfig.routes.process, label: 'Process', isActive: isProcessRouteActive },
        { to: appConfig.routes.successStories, label: 'Success Stories', isActive: isSuccessStoriesRouteActive },
        { to: appConfig.routes.industries, label: 'Industries', isActive: isIndustriesRouteActive },
        { to: appConfig.routes.about, label: 'About AMIVA', isActive: isAboutRouteActive },
        { to: appConfig.routes.team, label: 'Our Team', isActive: pathname.includes('/team') },
        { to: appConfig.routes.faq, label: 'FAQs', isActive: pathname.includes('/faq') },
    ].filter(
        (link) =>
            Boolean(link.to) &&
            (link.to !== appConfig.routes.team || appConfig.features.teamPage)
    );

    const serviceLinks = [
        { to: appConfig.routes.valuation, label: 'Get My Valuation', isActive: pathname.startsWith('/valuation') },
        {
            to: appConfig.routes.services?.businessValuation,
            label: 'Business Valuation',
            isActive: pathname.includes('/services/business-valuation'),
        },
        {
            to: appConfig.routes.services?.buyerSourcing,
            label: 'Buyer Sourcing',
            isActive: pathname.includes('/services/buyer-sourcing'),
        },
    ].filter((link) => Boolean(link.to));

    const linkClasses = (isActive) =>
        `text-sm font-medium transition-colors duration-200 ${isActive ? 'text-white' : 'text-white/70 hover:text-white'}`;

    const gridColumnsClass = serviceLinks.length > 0 ? 'md:grid-cols-3' : 'md:grid-cols-2';

    useEffect(() => {
        setShowPrivacyPolicy(pathname === appConfig.routes.privacyPolicy);
    }, [pathname]);

    return (
        <footer className="bg-gradient-to-br from-gray-900 via-gray-950 to-black text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="rounded-3xl border border-white/10 bg-white/5 p-10 sm:p-12">
                    <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
                        <div>
                            <div className="flex items-center gap-4">
                                <img
                                    src="/amiva-logo-light.png"
                                    alt="AMIVA Equity Partners"
                                    className="h-12 w-auto"
                                />
                                <div>
                                    <h3 className="text-xl font-semibold tracking-tight">{appConfig.site.brandName}</h3>
                                    <p className="text-xs uppercase tracking-[0.35em] text-white/60">Equity Partners</p>
                                </div>
                            </div>
                            <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/70 md:text-base">
                                Boutique sell-side advisory specializing in rapid, confidential transactions with clear buyer
                                communication and an AI-enabled diligence platform that keeps your deal team in sync.
                            </p>
                            <div className="mt-6 flex items-center gap-3">
                                {appConfig.site.socialLinks.map((link, index) => (
                                    <a
                                        key={`${link}-${index}`}
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors duration-200 hover:border-white hover:text-white"
                                    >
                                        <span className="sr-only">LinkedIn</span>
                                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                        </svg>
                                    </a>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                            <Link
                                to={appConfig.routes.valuation}
                                className="inline-flex items-center justify-center rounded-full bg-amiva-primary px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5"
                            >
                                Get My Valuation
                            </Link>
                            {calendlyUrl && (
                                <a
                                    href={calendlyUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white/80 transition-colors duration-200 hover:border-white hover:text-white"
                                >
                                    Book a Call
                                </a>
                            )}
                        </div>
                    </div>

                    <div className={`mt-12 grid grid-cols-1 gap-10 ${gridColumnsClass}`}>
                        <div>
                            <h4 className="text-lg font-semibold text-white">Navigation</h4>
                            <ul className="mt-4 space-y-3">
                                {navLinks.map((link) => (
                                    <li key={link.to}>
                                        <Link to={link.to} className={linkClasses(link.isActive)}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {serviceLinks.length > 0 && (
                            <div>
                                <h4 className="text-lg font-semibold text-white">Services</h4>
                                <ul className="mt-4 space-y-3">
                                    {serviceLinks.map((link) => (
                                        <li key={link.to}>
                                            <Link to={link.to} className={linkClasses(link.isActive)}>
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div>
                            <h4 className="text-lg font-semibold text-white">Contact</h4>
                            <ul className="mt-4 space-y-3 text-sm text-white/70">
                                <li>
                                    <p className="font-semibold text-white">24/7 Support</p>
                                    <p className="text-white/60">We stay on-call for active mandates and diligence cycles.</p>
                                </li>
                                <li>
                                    <a
                                        href={`tel:${appConfig.site.contactPhone}`}
                                        className="transition-colors duration-200 hover:text-white"
                                    >
                                        {appConfig.site.contactPhone}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href={`mailto:${appConfig.site.contactEmail}`}
                                        className="transition-colors duration-200 hover:text-white"
                                    >
                                        {appConfig.site.contactEmail}
                                    </a>
                                </li>
                                {appConfig.site.address && (
                                    <li>
                                        <p className="text-white/60">
                                            {appConfig.site.address.street}<br />
                                            {appConfig.site.address.city}, {appConfig.site.address.state} {appConfig.site.address.zip}<br />
                                            {appConfig.site.address.country}
                                        </p>
                                    </li>
                                )}
                                <li>
                                    <a
                                        href={appConfig.site.appUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-amiva-primary transition-colors duration-200 hover:text-white"
                                    >
                                        Access AMIVA+ Platform
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 text-sm text-white/70 md:flex-row md:items-center md:justify-between">
                        <p>© 2025 {appConfig.site.organizationName}. All rights reserved.</p>
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                            <button
                                type="button"
                                className="transition-colors duration-200 hover:text-white"
                                onClick={() => setShowPrivacyPolicy(true)}
                            >
                                Privacy Policy
                            </button>
                            <a
                                className="transition-colors duration-200 hover:text-white"
                                href={termsLink}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Terms of Use
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {showPrivacyPolicy && <PrivacyPolicy setShow={setShowPrivacyPolicy} />}
        </footer>
    );
}
